/**
 * DAG Wealth UI — bootstrap utilities.
 *
 * applyUiGlowUp() sets html[data-dag-ui="on"] and primes the initial theme
 * before React renders so there is no flash of unstyled content. Call it once
 * from index.tsx, before ReactDOM.createRoot.
 */

const LS_THEME = "dfo.theme";

type Theme = "light" | "dark";

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / storage disabled — ignore */
  }
}

/** Resolve the initial theme: saved choice, else OS preference. */
export function getInitialTheme(): Theme {
  const saved = safeGet(LS_THEME);
  if (saved === "light" || saved === "dark") return saved;
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

export function persistTheme(theme: Theme): void {
  safeSet(LS_THEME, theme);
}

/**
 * Bootstrap the brand layer on the root element. Call once, before React
 * renders, so there is no flash of the un-skinned UI.
 */
export function applyUiGlowUp(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.dagUi = "on";
  const initialTheme = getInitialTheme();
  root.setAttribute("data-bs-theme", initialTheme);
  root.classList.toggle("dark", initialTheme === "dark");

  // Apply saved privacy/density BEFORE first paint so sensitive figures never
  // flash unblurred (the hooks would otherwise only apply these in an effect).
  if (document.body) {
    if (safeGet("dfo.hideAmounts") === "1") document.body.classList.add("dag-hide-amounts");
    if (safeGet("dfo.compactTables") === "1") document.body.classList.add("dag-compact-tables");
  }
}
